import { useCallback, useMemo } from 'react';

import { Tabs } from 'antd';
import type { TabsProps } from 'antd';

import { TabLabel } from '@/shared/ui/TabLabel';

import { useChildTerms, useTaggedAssetsCount } from '@/features/glossary/model';

import { GlossaryAssetsTab, GlossaryTermsTab } from '@/widgets/glossary';

import styles from './GlossaryTermContent.module.css';

type GlossaryTermContentProps = {
  termFqn: string;
  currentTab: string;
  onTabChange: (tab: string) => void;
};

type TabKey = 'glossary_terms' | 'glossary_assets';

export const GlossaryTermContent = ({ termFqn, currentTab, onTabChange }: GlossaryTermContentProps) => {
  const { data: childTermsData, isLoading: isChildTermsLoading } = useChildTerms(termFqn);
  const { data: assetsCount, isLoading: isAssetsCountLoading } = useTaggedAssetsCount(termFqn);

  const TAB_COUNT_GETTERS = useMemo(
    () => ({
      glossary_terms: () => childTermsData?.paging?.total || 0,
      glossary_assets: () => assetsCount || 0,
    }),
    [childTermsData, assetsCount]
  );

  const TAB_LOADING_GETTERS = useMemo(
    () => ({
      glossary_terms: () => isChildTermsLoading,
      glossary_assets: () => isAssetsCountLoading,
    }),
    [isChildTermsLoading, isAssetsCountLoading]
  );

  const getTabCount = useCallback(
    (tabKey: TabKey): number => {
      return TAB_COUNT_GETTERS[tabKey]?.() || 0;
    },
    [TAB_COUNT_GETTERS]
  );

  const getTabLoading = useCallback(
    (tabKey: TabKey): boolean => {
      return TAB_LOADING_GETTERS[tabKey]?.() || false;
    },
    [TAB_LOADING_GETTERS]
  );

  const tabItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'glossary_terms',
        label: (
          <TabLabel
            label="용어"
            count={getTabCount('glossary_terms')}
            showCount={true}
            active={currentTab === 'glossary_terms'}
            isLoading={getTabLoading('glossary_terms')}
          />
        ),
        children: <GlossaryTermsTab termFqn={termFqn} />,
      },
      {
        key: 'glossary_assets',
        label: (
          <TabLabel
            label="자산"
            count={getTabCount('glossary_assets')}
            showCount={true}
            active={currentTab === 'glossary_assets'}
            isLoading={getTabLoading('glossary_assets')}
          />
        ),
        children: <GlossaryAssetsTab termFqn={termFqn} />,
      },
    ],
    [termFqn, currentTab, getTabCount, getTabLoading]
  );

  return (
    <div className={styles.content}>
      <Tabs activeKey={currentTab} onChange={onTabChange} items={tabItems} className={styles.tabs} destroyOnHidden />
    </div>
  );
};
